import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

export default function ScanPage() {
  const nav = useNavigate();
  const { code: routeCode } = useParams();
  const [code, setCode] = useState(routeCode || "");
  const [result, setResult] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const lookup = async (c: string) => {
    setErr(null);
    setResult(null);
    try {
      const res = await fetch(`${API}/scan/${encodeURIComponent(c.trim())}`, { credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).detail || res.statusText);
      setResult(await res.json());
    } catch (e: any) { setErr(String(e.message || e)); }
  };

  useEffect(() => { if (routeCode) lookup(routeCode); }, [routeCode]);
  useEffect(() => () => { scannerRef.current?.stop().catch(() => undefined); }, []);

  const startCamera = async () => {
    setScanning(true);
    setErr(null);
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
        (text) => {
          const m = text.match(/scan\/([A-Za-z0-9-]+)/);
          const c = m ? m[1] : text;
          setCode(c);
          scanner.stop().catch(() => undefined);
          setScanning(false);
          lookup(c);
        }, () => undefined);
    } catch (e: any) {
      setErr(`الكاميرا غير متاحة: ${e.message || e}`);
      setScanning(false);
    }
  };

  const b = result?.blend;

  return (
    <div className="max-w-xl space-y-4" data-testid="scan-page">
      <div className="bl-card">
        <div className="bl-eyebrow mb-2">📷 امسح QR أو اكتب الكود</div>
        <div className="flex gap-2">
          <input className="bl-input bl-en" placeholder="ORV-00001-S01" value={code}
            onChange={(e) => setCode(e.target.value)} data-testid="scan-code-input"
            onKeyDown={(e) => e.key === "Enter" && lookup(code)} />
          <button className="bl-btn primary" onClick={() => lookup(code)} data-testid="scan-lookup-btn">بحث</button>
          <button className="bl-btn ghost" onClick={startCamera} disabled={scanning} data-testid="scan-camera-btn">📷 امسح</button>
        </div>
        <div id="qr-reader" className="mt-3" style={{ maxWidth: 300 }} />
        {err && <div className="bl-sub mt-2" style={{ color: "var(--bl-danger)" }} data-testid="scan-error">{err}</div>}
      </div>

      {result && b && (
        <div className="bl-card" data-testid="scan-result">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bl-title bl-en">{result.kind === "sample" ? result.sample.code : result.kind === "batch" ? result.batch.code : b.code}</span>
            <span className="bl-pill">{b.status || "draft"}</span>
            <span className="bl-pill">{result.kind === "sample" ? "عينة" : result.kind === "batch" ? "تشغيلة" : "تركيبة"}</span>
          </div>
          <div className="font-extrabold mt-1">{b.name}</div>
          <div className="bl-sub bl-en mt-1">
            {b.body?.oil} {b.body?.pct}% + {b.direction?.oil} {b.direction?.pct}%
            {(b.boosters || []).slice(0, 3).map((x: any) => ` + ${x.material} ${x.pct}%`).join("")} · {b.concentration}
          </div>
          {(b.warnings || []).length > 0 && (
            <div className="flex gap-1 flex-wrap mt-2">
              {b.warnings.map((w: string, i: number) => <span key={i} className="bl-pill danger">{w}</span>)}
            </div>
          )}
          {result.kind === "sample" && (result.sample.test_results || []).map((r: any, i: number) => (
            <div key={i} className="bl-sub mt-1">⏱ بعد {r.afterHours} ساعة: {r.note}</div>
          ))}
          <div className="flex gap-2 mt-3">
            <button className="bl-btn primary" onClick={() => nav(`/pureh/blend/detail/${b.id}`)} data-testid="scan-open-detail">افتح الكارت</button>
          </div>
        </div>
      )}
    </div>
  );
}
